import { FirestoreDataConverter, Timestamp } from '@angular/fire/firestore';
import { PlanningCurrentHelper } from '../helpers/planning-current.helper';

export type PlanningMachineEventType = 'material_change' | 'format_change' |
  'material_and_format_change' | 'maintenance' | 'failure' | 'absence' | 'planned_stop' | 'breakdown';
export type PlanningMachineEventSource = 'manual' | 'automatic';

/** Planned and punched like a maintenance, only the label differs (a breakdown, which pauses work, is not one) */
const MAINTENANCE_TYPES: PlanningMachineEventType[] = ['maintenance', 'failure', 'absence', 'planned_stop'];

const CHANGEOVER_TYPES: PlanningMachineEventType[] = [
  'material_change',
  'format_change',
  'material_and_format_change',
];

/**
 * Manual machine event or automatically generated changeover
 */
export class PlanningMachineEvent {
  id: string;
  organizationId: string;
  machineId: number;
  oktoMachineId: string;
  type: PlanningMachineEventType;
  source: PlanningMachineEventSource;
  startAt: Date | null;
  endAt: Date | null;
  plannedStartAt: Date | null;
  plannedEndAt: Date | null;
  durationMinutes: number;
  targetMaterial: string;
  sourceMaterial: string;
  sourceFormat: string;
  targetFormat: string;
  relatedJobNumber: number;
  relatedOperationKey: string;
  comment: string;
  actualStart: Date | null;
  actualEnd: Date | null;
  actualStartedBy: string;
  actualEndedBy: string;
  createdBy: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  canPunch: boolean;
  /** Heads the punch queue of its machine, even where punching in any order makes every item punchable */
  isQueueHead: boolean;

  constructor(event?: Partial<PlanningMachineEvent>) {
    this.id = event?.id ?? '';
    this.organizationId = event?.organizationId ?? '';
    this.machineId = event?.machineId ?? 0;
    this.oktoMachineId = event?.oktoMachineId ?? '';
    this.type = event?.type ?? 'material_change';
    this.source = event?.source ?? 'manual';
    this.startAt = event?.startAt ?? null;
    this.endAt = event?.endAt ?? null;
    this.plannedStartAt = event?.plannedStartAt ?? null;
    this.plannedEndAt = event?.plannedEndAt ?? null;
    this.durationMinutes = event?.durationMinutes ?? 30;
    this.targetMaterial = event?.targetMaterial ?? '';
    this.sourceMaterial = event?.sourceMaterial ?? '';
    this.sourceFormat = event?.sourceFormat ?? '';
    this.targetFormat = event?.targetFormat ?? '';
    this.relatedJobNumber = event?.relatedJobNumber ?? 0;
    this.relatedOperationKey = event?.relatedOperationKey ?? '';
    this.comment = event?.comment ?? '';
    this.actualStart = event?.actualStart ?? null;
    this.actualEnd = event?.actualEnd ?? null;
    this.actualStartedBy = event?.actualStartedBy ?? '';
    this.actualEndedBy = event?.actualEndedBy ?? '';
    this.createdBy = event?.createdBy ?? '';
    this.createdAt = event?.createdAt ?? null;
    this.updatedAt = event?.updatedAt ?? null;
    this.canPunch = Boolean(event?.canPunch);
    this.isQueueHead = Boolean(event?.isQueueHead ?? event?.canPunch);
  }

  /**
   * Whether this event is a material or format change
   * @returns True for automatic changeover types
   */
  get isChangeover(): boolean {
    return CHANGEOVER_TYPES.includes(this.type);
  }

  /**
   * Whether the event is a maintenance or one of its categories (failure, absence, planned stop)
   * @returns True when it behaves like a maintenance
   */
  get isMaintenance(): boolean {
    return MAINTENANCE_TYPES.includes(this.type);
  }

  /**
   * Whether the event is punched like a task (changeover or maintenance)
   * @returns True when the event belongs to the punch queue
   */
  get isPunchable(): boolean {
    return this.isChangeover || this.isMaintenance;
  }

  /**
   * Whether the changeover has a declared real end
   * @returns True when finished
   */
  get isFinished(): boolean {
    return Boolean(this.actualEnd);
  }

  /**
   * End shown on the tile: real punch if finished, otherwise the planned end
   * @returns Event end
   */
  get displayEnd(): Date | null {
    if (this.actualEnd) {
      return this.actualEnd;
    }
    return this.endAt ?? (this.startAt && this.durationMinutes > 0 ?
      new Date(this.startAt.getTime() + this.durationMinutes * 60000) : null);
  }

  /**
   * Start shown on the tile: real punch if started, otherwise the planned start
   * @returns Event start
   */
  get displayStart(): Date | null {
    if (this.actualStart) {
      return this.actualStart;
    }
    const end = this.displayEnd;
    if (!this.actualEnd && end && this.durationMinutes > 0) {
      return new Date(end.getTime() - this.durationMinutes * 60000);
    }
    return this.startAt;
  }

  /**
   * Originally planned start entered when the maintenance was created
   * @returns Planned start, or null
   */
  get plannedStart(): Date | null {
    return this.plannedStartAt;
  }

  /**
   * Originally planned end entered when the maintenance was created
   * @returns Planned end, or null
   */
  get plannedEnd(): Date | null {
    return this.plannedEndAt ?? (this.plannedStartAt && this.durationMinutes > 0 ?
      new Date(this.plannedStartAt.getTime() + this.durationMinutes * 60000) : null);
  }

  /**
   * Whether the changeover or maintenance is the current work of its machine: the head of the punch queue
   * once its planned start has passed, even when it overruns its planned end, as for an operation
   * @param at Instant to test
   * @returns True when the unfinished, punchable event has started and nothing precedes it
   */
  isTheoreticallyInProgress(at: Date): boolean {
    return this.isPunchable && PlanningCurrentHelper.isCurrent(this, at);
  }

  /**
   * Converter used to read machine events from Firestore
   */
  static converter: FirestoreDataConverter<PlanningMachineEvent> = {
    toFirestore: (event: PlanningMachineEvent) => ({
      organizationId: event.organizationId,
      machineId: event.machineId,
      oktoMachineId: event.oktoMachineId,
      type: event.type,
      source: event.source,
      startAt: event.startAt,
      endAt: event.endAt,
      plannedStartAt: event.plannedStartAt,
      plannedEndAt: event.plannedEndAt,
      durationMinutes: event.durationMinutes,
      targetMaterial: event.targetMaterial,
      sourceMaterial: event.sourceMaterial,
      sourceFormat: event.sourceFormat,
      targetFormat: event.targetFormat,
      relatedJobNumber: event.relatedJobNumber,
      relatedOperationKey: event.relatedOperationKey,
      comment: event.comment,
      actualStart: event.actualStart,
      actualEnd: event.actualEnd,
      actualStartedBy: event.actualStartedBy,
      actualEndedBy: event.actualEndedBy,
      createdBy: event.createdBy,
      createdAt: event.createdAt,
      updatedAt: event.updatedAt,
    }),
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      return new PlanningMachineEvent({
        ...data,
        id: snapshot.id,
        startAt: (data['startAt'] as Timestamp)?.toDate() ?? null,
        endAt: (data['endAt'] as Timestamp)?.toDate() ?? null,
        plannedStartAt: (data['plannedStartAt'] as Timestamp)?.toDate() ?? null,
        plannedEndAt: (data['plannedEndAt'] as Timestamp)?.toDate() ?? null,
        actualStart: (data['actualStart'] as Timestamp)?.toDate() ?? null,
        actualEnd: (data['actualEnd'] as Timestamp)?.toDate() ?? null,
        createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? null,
        updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? null,
      });
    },
  };
}
